/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import React from "react";
import { Button } from "reactstrap";

export interface RadioButtonProps {
    value: string | boolean,
    current: any,
    name: string,
    className: string,
    onChange: (name, value) => void,
    children: string | React.ReactElement,
    disabled: boolean,
    color: string,
}
const isBootstrapColor = (color: string) => {
    return ["primary", "secondary", "success", "danger", "warning", "info", "light", "dark"].includes(color);
};
export function RadioButton({
    value, current, name, color = "primary", className, onChange, children, disabled
}: RadioButtonProps) {
    let buttonArgs = {
        color: "light"
    } as any;
    if (!color || isBootstrapColor(color)) {
        buttonArgs = {
            color: color || "light",
            outline: current !== value,
        };
    } else if (color) {
        if (current !== value) {
            buttonArgs = {
                css: css`color: black; border-color: ${color}; opacity: 0.8; background-color: transparent; &:hover, &:active, &:focus { background-color: ${color}; opacity: 1; }`
            };
        } else {
            buttonArgs = {
                css: css`border-color: ${color};background-color: ${color};`
            };

        }
    }
    return (
        <Button {...buttonArgs} disabled={disabled} className={["btn-radio", className || ""].join(" ")} onClick={() => onChange(name, value)}>
            {children}
        </Button>
    );
}

export interface selectedValueProps {
    value?: string | boolean,
    label?: string
}

export interface RadioButtonsProps {
    options: { value: string | boolean, text?: string, label?: string, color?: string, className?: string, disabled?: boolean }[],
    current?: string,
    name: string,
    color?: string,
    onChange: (name, value) => void,
    className?: string,
    disabled?: boolean,
    default_value?: string,
}

const isTouchPointer = () => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
        return false;
    }
    return window.matchMedia("(hover: none) and (pointer: coarse)").matches;
};

export function RadioButtons({
    options,
    current,
    name,
    color = "primary",
    onChange,
    className = "",
    disabled = false,
    default_value,
}: RadioButtonsProps) {
    const selectedValue: selectedValueProps = options.find((i) => String(i.value) === String(current === undefined ? default_value : current)) ?? {};

    return (
        <div className="d-flex flex-wrap gap-1">
            {options.map((i) => (
                <div key={String(i.value)} css={css`flex: 0 0 auto; min-width: 5.5rem;`}>
                    <RadioButton
                        color={i.color ? i.color : color}
                        value={i.value}
                        name={name}
                        disabled={i.disabled || disabled}
                        current={selectedValue.value}
                        className={`w-100 text-nowrap ${i.className} ${className}`}
                        onChange={(name, value) => {
                            const newValue = current === value ? undefined : value;
                            onChange(name, newValue);
                            if (isTouchPointer()) {
                                (document.activeElement as HTMLElement | null)?.blur();
                            }
                        }}
                    >
                        {(i.label || i.text) as string}
                    </RadioButton>
                </div>
            ))}
        </div>
    );
}
